import React from 'react'

import {
  canConsumeVerticalScroll,
  findVerticalScrollableAncestor,
  getWheelDeltaYInPixels,
} from '../dataGridScroll.utils'

/**
 * useTransferVerticalScroll
 *
 * Transfers vertical wheel scrolling to the nearest scrollable ancestor when
 * the inner scroll container has reached its top or bottom edge.
 *
 * This is useful for nested scroll regions such as `DataGrid`, where the grid
 * should keep its own vertical scroll while it has room, but should allow the
 * surrounding page or panel to continue scrolling once the grid is pinned at
 * an edge.
 *
 * @param rootRef Ref for the root element that contains the nested scroll region.
 *                The ancestor lookup starts from this element.
 * @param scrollContainerSelector Selector used to find the inner scroll container
 *                                inside `rootRef` that receives wheel events.
 *
 * @example
 * const rootRef = React.useRef<HTMLDivElement | null>(null)
 * useTransferVerticalScroll(rootRef, '.MuiDataGrid-virtualScroller')
 */
const useTransferVerticalScroll = (
  rootRef: React.RefObject<HTMLElement | null>,
  scrollContainerSelector: string,
) => {
  React.useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const scroller = root.querySelector<HTMLElement>(scrollContainerSelector)
    if (!scroller) return

    const onWheel = (event: WheelEvent) => {
      if (
        event.defaultPrevented ||
        event.ctrlKey ||
        event.shiftKey ||
        Math.abs(event.deltaY) <= Math.abs(event.deltaX)
      ) {
        return
      }

      if (canConsumeVerticalScroll(scroller, event.deltaY)) {
        return
      }

      const ancestor = findVerticalScrollableAncestor(root)
      if (!ancestor || !canConsumeVerticalScroll(ancestor, event.deltaY)) {
        return
      }

      event.preventDefault()
      ancestor.scrollBy({
        top: getWheelDeltaYInPixels(
          event,
          ancestor.clientHeight || window.innerHeight,
        ),
      })
    }

    scroller.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      scroller.removeEventListener('wheel', onWheel)
    }
  }, [rootRef, scrollContainerSelector])
}

export default useTransferVerticalScroll
